import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../api';

const CATEGORY_COLORS = {
  Groceries: '#22c55e', Dining: '#f97316', Shopping: '#8b5cf6', Transportation: '#3b82f6',
  Utilities: '#eab308', Entertainment: '#ec4899', Healthcare: '#ef4444', Income: '#10b981',
  Transfer: '#6b7280', Subscription: '#06b6d4', Travel: '#f59e0b', Other: '#71717a',
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function TransactionReports({ refreshKey, extraRefreshKey }) {
  const [transactions, setTransactions] = useState([]);
  const [categoryConfigs, setCategoryConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthsBack, setMonthsBack] = useState(6);
  const [hoveredMonth, setHoveredMonth] = useState(null);

  useEffect(() => {
    async function fetch() {
      try {
        const [txRes, configRes] = await Promise.all([
          apiFetch('/api/simplefin/transactions'),
          apiFetch('/api/category-configs'),
        ]);
        if (txRes.ok) setTransactions(await txRes.json());
        if (configRes.ok) setCategoryConfigs(await configRes.json());
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    fetch();
  }, [refreshKey, extraRefreshKey]);

  function isNotReportableCategory(cat) {
    if (!cat) return false;
    const cfg = categoryConfigs.find((c) => c.category === cat)
      || categoryConfigs.find((c) => cat.startsWith(c.category + ':'));
    return cfg ? cfg.autoReportable === false : false;
  }

  const data = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = monthsBack - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }

    // Monthly spending & income
    const monthly = {};
    const categoryMonthly = {};
    const dailySpending = {};
    const allCategories = new Set();

    for (const month of months) {
      monthly[month] = { spending: 0, income: 0, count: 0 };
      categoryMonthly[month] = {};
    }

    for (const tx of transactions) {
      const date = tx.posted || tx.transactedAt;
      if (!date) continue;
      const month = date.slice(0, 7);
      const day = date.slice(0, 10);
      const amt = parseFloat(tx.amount) || 0;
      const reportable = tx.isReportable !== false && !isNotReportableCategory(tx.category);

      if (monthly[month]) {
        monthly[month].count++;
        if (amt < 0 && reportable) {
          monthly[month].spending += Math.abs(amt);
          const cat = tx.category || 'Uncategorized';
          allCategories.add(cat);
          categoryMonthly[month][cat] = (categoryMonthly[month][cat] || 0) + Math.abs(amt);
        }
        if (amt > 0 && reportable) {
          monthly[month].income += amt;
        }
      }

      // Daily spending (last 90 days)
      if (amt < 0 && reportable) {
        const txDate = new Date(date);
        const daysAgo = Math.floor((now - txDate) / 86400000);
        if (daysAgo <= 90) {
          dailySpending[day] = (dailySpending[day] || 0) + Math.abs(amt);
        }
      }
    }

    // Average daily spending
    const dailyValues = Object.values(dailySpending);
    const avgDaily = dailyValues.length > 0 ? dailyValues.reduce((a, b) => a + b, 0) / dailyValues.length : 0;

    // Category totals (across all shown months)
    const categoryTotals = {};
    for (const month of months) {
      for (const [cat, val] of Object.entries(categoryMonthly[month])) {
        categoryTotals[cat] = (categoryTotals[cat] || 0) + val;
      }
    }

    return { months, monthly, categoryMonthly, categoryTotals, allCategories: [...allCategories], dailySpending, avgDaily };
  }, [transactions, monthsBack, categoryConfigs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <section className="card"><h2>Transaction Reports</h2><p className="loading">Loading...</p></section>;
  }

  function fmt(val) { return val.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }); }

  // Monthly bar chart dimensions
  const barW = 600, barH = 180, barPadL = 60, barPadR = 20, barPadT = 10, barPadB = 25;
  const barChartW = barW - barPadL - barPadR;
  const barChartH = barH - barPadT - barPadB;
  const maxMonthly = Math.max(...data.months.map(m => Math.max(data.monthly[m].spending, data.monthly[m].income)), 1);
  const barGroupW = barChartW / data.months.length;

  // Category stacked bar chart
  const sortedCats = Object.entries(data.categoryTotals).sort((a, b) => b[1] - a[1]);
  const totalSpending = sortedCats.reduce((s, [, v]) => s + v, 0);

  // Daily heatmap (last 90 days, 7 rows x ~13 cols)
  const heatmapDays = [];
  for (let i = 89; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    heatmapDays.push({ date: key, value: data.dailySpending[key] || 0, day: d.getDay() });
  }
  const maxDaily = Math.max(...heatmapDays.map(d => d.value), 1);

  return (
    <section className="card tr-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Transaction Reports</h2>
        <div className="nw-range-tabs">
          {[3, 6, 12].map(n => (
            <button key={n} className={`nw-range-btn${monthsBack === n ? ' active' : ''}`} onClick={() => setMonthsBack(n)}>
              {n}M
            </button>
          ))}
        </div>
      </div>

      {/* Monthly Income vs Spending */}
      <h3>Monthly Income vs Spending</h3>
      {(() => {
        // Net line data
        const netValues = data.months.map(m => data.monthly[m].income - data.monthly[m].spending);
        const maxNet = Math.max(...netValues.map(Math.abs), 1);
        const allMax = Math.max(maxMonthly, maxNet);

        // Y position for net line (centered, can go negative)
        const netBaseline = barPadT + barChartH;
        function netY(val) {
          return netBaseline - (val / allMax) * barChartH;
        }

        const netLinePath = data.months.map((month, i) => {
          const net = data.monthly[month].income - data.monthly[month].spending;
          const px = barPadL + i * barGroupW + barGroupW / 2;
          const py = netY(net);
          return `${i === 0 ? 'M' : 'L'}${px.toFixed(1)},${py.toFixed(1)}`;
        }).join(' ');

        return (
          <div className="nw-chart-wrapper" style={{ position: 'relative' }}>
            <svg viewBox={`0 0 ${barW} ${barH}`} preserveAspectRatio="xMidYMid meet"
              style={{ width: '100%', height: 'auto', display: 'block', minHeight: 180 }}>
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
                const yy = barPadT + barChartH * (1 - pct);
                return <g key={i}>
                  <line x1={barPadL} y1={yy} x2={barW - barPadR} y2={yy} stroke="#2a2b35" strokeWidth="1" />
                  <text x={barPadL - 6} y={yy + 4} textAnchor="end" fill="#71717a" fontSize="9">{fmt(allMax * pct)}</text>
                </g>;
              })}

              {/* Bars */}
              {data.months.map((month, i) => {
                const gx = barPadL + i * barGroupW;
                const bw = barGroupW * 0.3;
                const sH = (data.monthly[month].spending / allMax) * barChartH;
                const iH = (data.monthly[month].income / allMax) * barChartH;
                const label = MONTH_NAMES[parseInt(month.slice(5)) - 1];
                const isHovered = hoveredMonth === i;
                return <g key={month}>
                  {/* Hover background */}
                  <rect x={gx} y={barPadT} width={barGroupW} height={barChartH}
                    fill={isHovered ? 'rgba(255,255,255,0.03)' : 'transparent'}
                    onMouseEnter={() => setHoveredMonth(i)}
                    onMouseLeave={() => setHoveredMonth(null)}
                    style={{ cursor: 'pointer' }}
                  />
                  <rect x={gx + barGroupW * 0.1} y={barPadT + barChartH - iH} width={bw} height={iH} fill="#22c55e" rx={2} opacity={0.8} />
                  <rect x={gx + barGroupW * 0.1 + bw + 2} y={barPadT + barChartH - sH} width={bw} height={sH} fill="#ef4444" rx={2} opacity={0.8} />
                  <text x={gx + barGroupW / 2} y={barH - 5} textAnchor="middle" fill="#71717a" fontSize="10">{label}</text>
                </g>;
              })}

              {/* Net income line */}
              <path d={netLinePath} fill="none" stroke="#eab308" strokeWidth="2" strokeLinejoin="round" />

              {/* Net income dots */}
              {data.months.map((month, i) => {
                const net = data.monthly[month].income - data.monthly[month].spending;
                const px = barPadL + i * barGroupW + barGroupW / 2;
                const py = netY(net);
                return <circle key={`net-${month}`} cx={px} cy={py} r={hoveredMonth === i ? 5 : 3}
                  fill="#eab308" stroke="#0f1117" strokeWidth="1.5"
                  onMouseEnter={() => setHoveredMonth(i)}
                  onMouseLeave={() => setHoveredMonth(null)}
                  style={{ cursor: 'pointer' }}
                />;
              })}

              {/* Hover tooltip */}
              {hoveredMonth !== null && (() => {
                const month = data.months[hoveredMonth];
                if (!month) return null;
                const m = data.monthly[month];
                const net = m.income - m.spending;
                const px = barPadL + hoveredMonth * barGroupW + barGroupW / 2;
                const tooltipW = 130;
                const tooltipX = px > barW / 2 ? px - tooltipW - 10 : px + 10;
                return <g>
                  <line x1={px} y1={barPadT} x2={px} y2={barPadT + barChartH} stroke="#71717a" strokeWidth="1" strokeDasharray="3,3" />
                  <rect x={tooltipX} y={barPadT} width={tooltipW} height={58} rx={6} fill="#1a1b23" stroke="#3a3b45" />
                  <text x={tooltipX + 8} y={barPadT + 14} fill="#22c55e" fontSize="10" fontWeight="600">Income: {fmt(m.income)}</text>
                  <text x={tooltipX + 8} y={barPadT + 28} fill="#ef4444" fontSize="10" fontWeight="600">Spending: {fmt(m.spending)}</text>
                  <text x={tooltipX + 8} y={barPadT + 42} fill="#eab308" fontSize="10" fontWeight="600">Net: {net >= 0 ? '+' : ''}{fmt(net)}</text>
                  <text x={tooltipX + 8} y={barPadT + 54} fill="#71717a" fontSize="9">{MONTH_NAMES[parseInt(month.slice(5)) - 1]} {month.slice(0, 4)}</text>
                </g>;
              })()}
            </svg>
          </div>
        );
      })()}
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 4 }}>
        <span className="wd-legend-item"><span className="wd-legend-dot" style={{ background: '#22c55e' }} />Income</span>
        <span className="wd-legend-item"><span className="wd-legend-dot" style={{ background: '#ef4444' }} />Spending</span>
        <span className="wd-legend-item"><span className="wd-legend-dot" style={{ background: '#eab308' }} />Net</span>
      </div>

      {/* Category Breakdown */}
      <h3 style={{ marginTop: 16 }}>Spending by Category</h3>
      <div className="tr-category-bars">
        {sortedCats.map(([cat, val]) => {
          const pct = totalSpending > 0 ? (val / totalSpending) * 100 : 0;
          return (
            <div key={cat} className="tr-cat-bar-row">
              <span className="tr-cat-bar-label">{cat}</span>
              <div className="tr-cat-bar-track">
                <div className="tr-cat-bar-fill" style={{ width: `${pct}%`, background: CATEGORY_COLORS[cat] || '#71717a' }} />
              </div>
              <span className="tr-cat-bar-value">{fmt(val)}</span>
              <span className="tr-cat-bar-pct">{pct.toFixed(0)}%</span>
            </div>
          );
        })}
        {sortedCats.length === 0 && <p style={{ color: '#71717a', fontSize: 13 }}>No spending data</p>}
      </div>

      {/* Category Trend (stacked area by month) */}
      <h3 style={{ marginTop: 16 }}>Category Trends</h3>
      <div className="nw-chart-wrapper">
        <svg viewBox={`0 0 ${barW} ${barH}`} className="nw-chart-svg">
          {data.months.map((month, i) => {
            const gx = barPadL + i * barGroupW + barGroupW * 0.1;
            const bw = barGroupW * 0.8;
            let yOffset = 0;
            const maxCatMonth = Math.max(...data.months.map(m => Object.values(data.categoryMonthly[m]).reduce((a, b) => a + b, 0)), 1);
            const label = MONTH_NAMES[parseInt(month.slice(5)) - 1];
            return <g key={month}>
              {sortedCats.map(([cat]) => {
                const val = data.categoryMonthly[month][cat] || 0;
                const h = (val / maxCatMonth) * barChartH;
                const bar = <rect key={cat} x={gx} y={barPadT + barChartH - yOffset - h} width={bw} height={h}
                  fill={CATEGORY_COLORS[cat] || '#71717a'} opacity={0.85} />;
                yOffset += h;
                return bar;
              })}
              <text x={gx + bw / 2} y={barH - 5} textAnchor="middle" fill="#71717a" fontSize="10">{label}</text>
            </g>;
          })}
        </svg>
        <div className="tr-cat-legend">
          {sortedCats.slice(0, 8).map(([cat]) => (
            <span key={cat} className="wd-legend-item">
              <span className="wd-legend-dot" style={{ background: CATEGORY_COLORS[cat] || '#71717a' }} />{cat}
            </span>
          ))}
        </div>
      </div>

      {/* Daily Spending Heatmap */}
      <h3 style={{ marginTop: 16 }}>Daily Spending (Last 90 Days)</h3>
      <div className="tr-heatmap-wrapper">
        <div className="tr-heatmap">
          {heatmapDays.map((d, i) => {
            const intensity = d.value > 0 ? Math.min(1, d.value / maxDaily) : 0;
            const color = intensity === 0 ? '#1a1b23' : `rgba(239, 68, 68, ${0.2 + intensity * 0.8})`;
            return (
              <div key={d.date} className="tr-heatmap-cell" style={{ background: color }}
                title={`${d.date}: ${fmt(d.value)}`} />
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#71717a', marginTop: 4 }}>
          <span>90 days ago</span>
          <span>Avg: {fmt(data.avgDaily)}/day</span>
          <span>Today</span>
        </div>
      </div>

      {/* Monthly Summary Table */}
      <h3 style={{ marginTop: 16 }}>Monthly Summary</h3>
      <div className="tr-summary-table">
        <div className="tr-table-header">
          <span>Month</span><span>Income</span><span>Spending</span><span>Net</span><span>Txns</span>
        </div>
        {data.months.map(month => {
          const m = data.monthly[month];
          const net = m.income - m.spending;
          return (
            <div key={month} className="tr-table-row">
              <span>{MONTH_NAMES[parseInt(month.slice(5)) - 1]} {month.slice(0, 4)}</span>
              <span className="positive">{fmt(m.income)}</span>
              <span className="negative">{fmt(m.spending)}</span>
              <span className={net >= 0 ? 'positive' : 'negative'}>{fmt(net)}</span>
              <span>{m.count}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default TransactionReports;
